import React, { useEffect, useState } from "react";
import { formFields } from "../utils/api";
import { useToast } from "../contexts/ToastContext";
import PaginationControls from "../components/PaginationControls";
import {
  COLOR_OPTIONS,
  defaultColorRules,
  supportsColorRules,
} from "../utils/fieldColorRules";

const PAGE_SIZE = 10;

const TYPE_OPTIONS = [
  { value: "yes_no", label: "Yes / No" },
  { value: "positive_integer", label: "Positive whole number" },
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "duration", label: "Duration (minutes)" },
  { value: "select", label: "Multiple choice" },
];

const emptyForm = {
  label: "",
  fieldType: "yes_no",
  helpText: "",
  isRequired: false,
  showOnDashboardByDefault: true,
  colorRules: defaultColorRules("yes_no"),
  minValue: "",
  maxValue: "",
  maxLength: "",
  choices: "",
};

const choicesToOptions = (choices) =>
  choices
    .split("\n")
    .map((choice) => choice.trim())
    .filter(Boolean)
    .map((choice) => ({ label: choice, value: choice }));

const ColorSelect = ({ value, onChange, ariaLabel }) => (
  <select
    value={value || "blue"}
    onChange={(event) => onChange(event.target.value)}
    aria-label={ariaLabel}
    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
  >
    {COLOR_OPTIONS.map((color) => (
      <option key={color.value} value={color.value}>
        {color.label}
      </option>
    ))}
  </select>
);

const ColorRulesEditor = ({ fieldType, options, value, onChange }) => {
  if (!supportsColorRules(fieldType)) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
        <p className="text-sm font-medium text-gray-800">Dashboard colors</p>
        <p className="mt-1 text-xs text-gray-500">
          Text and date fields use neutral dashboard styling.
        </p>
      </div>
    );
  }

  const rules = value || defaultColorRules(fieldType, options);
  const updateRules = (changes) => onChange({ ...rules, ...changes });
  const isThresholdType = ["positive_integer", "duration", "time"].includes(
    fieldType
  );
  const thresholdInputType = fieldType === "time" ? "time" : "number";
  const unit = fieldType === "duration" ? " minutes" : "";
  const higherIsBetter = rules.direction !== "lower";

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={Boolean(rules.enabled)}
          onChange={(event) => updateRules({ enabled: event.target.checked })}
          className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600"
        />
        <span>
          <span className="block text-sm font-medium text-gray-800">
            Use dashboard highlight colors
          </span>
          <span className="mt-0.5 block text-xs text-gray-500">
            Missing answers remain neutral gray.
          </span>
        </span>
      </label>

      {rules.enabled && fieldType === "yes_no" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm text-gray-700">
            <span className="mb-1 block">Yes color</span>
            <ColorSelect
              value={rules.yesColor || "green"}
              onChange={(yesColor) => updateRules({ yesColor })}
              ariaLabel="Yes answer color"
            />
          </label>
          <label className="text-sm text-gray-700">
            <span className="mb-1 block">No color</span>
            <ColorSelect
              value={rules.noColor || "red"}
              onChange={(noColor) => updateRules({ noColor })}
              ariaLabel="No answer color"
            />
          </label>
        </div>
      )}

      {rules.enabled && fieldType === "select" && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Choice colors
          </p>
          {options.length === 0 ? (
            <p className="text-xs text-gray-500">Enter choices above first.</p>
          ) : (
            options.map((option) => (
              <div
                key={option.value}
                className="flex items-center justify-between gap-3"
              >
                <span className="min-w-0 truncate text-sm text-gray-700">
                  {option.label}
                </span>
                <ColorSelect
                  value={rules.optionColors?.[String(option.value)] || "blue"}
                  onChange={(color) =>
                    updateRules({
                      optionColors: {
                        ...(rules.optionColors || {}),
                        [String(option.value)]: color,
                      },
                    })
                  }
                  ariaLabel={`${option.label} color`}
                />
              </div>
            ))
          )}
        </div>
      )}

      {rules.enabled && isThresholdType && (
        <div className="space-y-3">
          <label className="block text-sm text-gray-700">
            <span className="mb-1 block">Scoring direction</span>
            <select
              value={rules.direction || "higher"}
              onChange={(event) => updateRules({ direction: event.target.value })}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2"
            >
              <option value="higher">Higher is better</option>
              <option value="lower">Lower is better</option>
            </select>
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm text-gray-700">
              <span className="mb-1 block">
                Green when {higherIsBetter ? "at least" : "at most"}
              </span>
              <input
                type={thresholdInputType}
                min={fieldType === "time" ? undefined : "0"}
                step={fieldType === "time" ? undefined : "1"}
                required
                value={rules.greenThreshold ?? ""}
                onChange={(event) =>
                  updateRules({ greenThreshold: event.target.value })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
              {unit && <span className="mt-1 block text-xs text-gray-500">{unit.trim()}</span>}
            </label>
            <label className="text-sm text-gray-700">
              <span className="mb-1 block">
                Yellow when {higherIsBetter ? "at least" : "at most"}
              </span>
              <input
                type={thresholdInputType}
                min={fieldType === "time" ? undefined : "0"}
                step={fieldType === "time" ? undefined : "1"}
                required
                value={rules.yellowThreshold ?? ""}
                onChange={(event) =>
                  updateRules({ yellowThreshold: event.target.value })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
              {unit && <span className="mt-1 block text-xs text-gray-500">{unit.trim()}</span>}
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Values outside the yellow range are red.
          </p>
        </div>
      )}
    </div>
  );
};

const FormFieldsPage = () => {
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editForm, setEditForm] = useState({
    label: "",
    helpText: "",
    isRequired: false,
    showOnDashboardByDefault: true,
    colorRules: defaultColorRules("yes_no"),
  });
  const [currentPage, setCurrentPage] = useState(1);
  const { showSuccess, showError } = useToast();

  const loadFields = async () => {
    try {
      const response = await formFields.list(true);
      setFields(response.data);
    } catch (error) {
      showError("Failed to load daily form fields");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFields();
  }, []);

  const totalPages = Math.max(1, Math.ceil(fields.length / PAGE_SIZE));
  const paginatedFields = fields.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const options = form.choices
        .split("\n")
        .map((choice) => choice.trim())
        .filter(Boolean);
      await formFields.create({
        label: form.label,
        fieldType: form.fieldType,
        helpText: form.helpText,
        isRequired: form.isRequired,
        showOnDashboardByDefault: form.showOnDashboardByDefault,
        colorRules: form.colorRules,
        minValue: form.minValue,
        maxValue: form.maxValue,
        maxLength: form.maxLength,
        options,
      });
      showSuccess("Field added to the daily form");
      setForm(emptyForm);
      setShowCreateForm(false);
      setCurrentPage(Math.max(1, Math.ceil((fields.length + 1) / PAGE_SIZE)));
      await loadFields();
    } catch (error) {
      showError(error.response?.data?.error || "Failed to create field");
    } finally {
      setSaving(false);
    }
  };

  const toggleField = async (field) => {
    try {
      await formFields.update(field.id, { isActive: !field.is_active });
      showSuccess(field.is_active ? "Field disabled" : "Field enabled");
      await loadFields();
    } catch (error) {
      showError(error.response?.data?.error || "Failed to update field");
    }
  };

  const toggleDashboardDefault = async (field) => {
    try {
      await formFields.update(field.id, {
        showOnDashboardByDefault: !field.show_on_dashboard_by_default,
      });
      showSuccess(
        field.show_on_dashboard_by_default
          ? "Field hidden from dashboards by default"
          : "Field shown on dashboards by default"
      );
      await loadFields();
    } catch (error) {
      showError(
        error.response?.data?.error ||
          "Failed to update the dashboard default"
      );
    }
  };

  const openEdit = (field) => {
    setEditingField(field);
    setEditForm({
      label: field.label,
      helpText: field.help_text || "",
      isRequired: field.is_required,
      showOnDashboardByDefault: field.show_on_dashboard_by_default,
      colorRules:
        field.color_rules ||
        defaultColorRules(field.field_type, field.options || []),
    });
  };

  const closeEdit = () => {
    setEditingField(null);
    setEditForm({
      label: "",
      helpText: "",
      isRequired: false,
      showOnDashboardByDefault: true,
      colorRules: defaultColorRules("yes_no"),
    });
  };

  const handleEdit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await formFields.update(editingField.id, editForm);
      showSuccess("Field details updated");
      closeEdit();
      await loadFields();
    } catch (error) {
      showError(error.response?.data?.error || "Failed to edit field");
    } finally {
      setSaving(false);
    }
  };

  const updateOrder = async (field, direction) => {
    try {
      await formFields.move(field.id, direction);
      await loadFields();
    } catch (error) {
      showError(error.response?.data?.error || "Failed to reorder field");
    }
  };

  const typeLabel = (type) =>
    TYPE_OPTIONS.find((option) => option.value === type)?.label || type;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">Loading form fields...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 pb-10 pt-20">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Daily Form Fields</h1>
              <p className="text-sm text-gray-600 mt-1">
                Built-in and custom fields use the same controls. Edit, reorder, show or hide by default, or disable any field without losing answers.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowCreateForm((value) => !value)}
              className="shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {showCreateForm ? "Cancel" : "Add field"}
            </button>
          </div>
        </div>

        {showCreateForm && (
          <form
            onSubmit={handleCreate}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 space-y-4"
          >
            <h2 className="text-lg font-semibold">Create a field</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field name</label>
              <input
                required
                maxLength={100}
                value={form.label}
                onChange={(event) => setForm({ ...form, label: event.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Example: Exercise completed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Answer type</label>
              <select
                value={form.fieldType}
                onChange={(event) => {
                  const fieldType = event.target.value;
                  setForm({
                    ...form,
                    fieldType,
                    colorRules: defaultColorRules(fieldType),
                  });
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-3 bg-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Help text (optional)</label>
              <input
                maxLength={255}
                value={form.helpText}
                onChange={(event) => setForm({ ...form, helpText: event.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="A short hint shown below the field"
              />
            </div>

            {(form.fieldType === "positive_integer" || form.fieldType === "duration") && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minimum</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.minValue}
                    onChange={(event) => setForm({ ...form, minValue: event.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-3"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maximum</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.maxValue}
                    onChange={(event) => setForm({ ...form, maxValue: event.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-3"
                    placeholder="No limit"
                  />
                </div>
              </div>
            )}

            {(form.fieldType === "text" || form.fieldType === "textarea") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maximum characters</label>
                <input
                  type="number"
                  min="1"
                  max="2000"
                  step="1"
                  value={form.maxLength}
                  onChange={(event) => setForm({ ...form, maxLength: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-3"
                  placeholder={form.fieldType === "textarea" ? "1000" : "255"}
                />
              </div>
            )}

            {form.fieldType === "select" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Choices</label>
                <textarea
                  required
                  rows="4"
                  value={form.choices}
                  onChange={(event) => setForm({ ...form, choices: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-3"
                  placeholder={"Enter one choice per line\nExample: In person\nOnline"}
                />
                <p className="text-xs text-gray-500 mt-1">Enter 2–20 choices, one per line.</p>
              </div>
            )}

            <ColorRulesEditor
              fieldType={form.fieldType}
              options={
                form.fieldType === "select"
                  ? choicesToOptions(form.choices)
                  : []
              }
              value={form.colorRules}
              onChange={(colorRules) => setForm({ ...form, colorRules })}
            />

            <label className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
              <input
                type="checkbox"
                checked={form.isRequired}
                onChange={(event) => setForm({ ...form, isRequired: event.target.checked })}
                className="h-5 w-5 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm font-medium text-gray-800">Students must answer this field</span>
            </label>

            <label className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
              <input
                type="checkbox"
                checked={form.showOnDashboardByDefault}
                onChange={(event) =>
                  setForm({
                    ...form,
                    showOnDashboardByDefault: event.target.checked,
                  })
                }
                className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600"
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  Show on dashboard by default
                </span>
                <span className="mt-0.5 block text-xs text-gray-500">
                  Each user can still show or hide it from their Column Settings.
                </span>
              </span>
            </label>

            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-lg bg-blue-600 px-4 py-3 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Creating..." : "Create field"}
            </button>
          </form>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    "#",
                    "Field name",
                    "Type",
                    "Helper text",
                    "Status",
                    "Dashboard default",
                    "Order",
                    "Actions",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {paginatedFields.map((field, index) => (
                  <tr key={field.id} className={field.is_active ? "" : "bg-orange-50"}>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                      {(currentPage - 1) * PAGE_SIZE + index + 1}
                    </td>
                    <td className="min-w-48 px-4 py-3 font-medium text-gray-900">
                      {field.label}
                      {field.is_required && (
                        <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">
                          Required
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                      {typeLabel(field.field_type)}
                    </td>
                    <td className="max-w-64 px-4 py-3 text-gray-600">
                      <div className="truncate" title={field.help_text || ""}>
                        {field.help_text || "-"}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          field.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {field.is_active ? "Enabled" : "Disabled"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleDashboardDefault(field)}
                        aria-label={`${
                          field.show_on_dashboard_by_default ? "Hide" : "Show"
                        } ${field.label} on dashboards by default`}
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          field.show_on_dashboard_by_default
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {field.show_on_dashboard_by_default ? "Shown" : "Hidden"}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => updateOrder(field, -1)}
                          className="rounded-md border px-2 py-1 text-gray-600 hover:bg-gray-50"
                          aria-label={`Move ${field.label} earlier`}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => updateOrder(field, 1)}
                          className="rounded-md border px-2 py-1 text-gray-600 hover:bg-gray-50"
                          aria-label={`Move ${field.label} later`}
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(field)}
                          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleField(field)}
                          className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${
                            field.is_active
                              ? "bg-orange-600 hover:bg-orange-700"
                              : "bg-green-600 hover:bg-green-700"
                          }`}
                        >
                          {field.is_active ? "Disable" : "Enable"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={fields.length}
            pageSize={PAGE_SIZE}
            itemLabel="fields"
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {editingField && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <form
            onSubmit={handleEdit}
            className="max-h-[calc(100vh-2rem)] w-full max-w-lg space-y-4 overflow-y-auto rounded-xl bg-white p-5 shadow-xl sm:p-6"
          >
            <div>
              <h2 className="text-xl font-bold text-gray-900">Edit daily form field</h2>
              <p className="mt-1 text-sm text-gray-600">
                The answer type and stored values will not change.
              </p>
            </div>
            <div>
              <label htmlFor="edit-field-name" className="mb-1 block text-sm font-medium text-gray-700">
                Field name
              </label>
              <input
                id="edit-field-name"
                required
                maxLength={100}
                value={editForm.label}
                onChange={(event) => setEditForm({ ...editForm, label: event.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div>
              <label htmlFor="edit-field-help" className="mb-1 block text-sm font-medium text-gray-700">
                Helper text (optional)
              </label>
              <textarea
                id="edit-field-help"
                rows="3"
                maxLength={255}
                value={editForm.helpText}
                onChange={(event) => setEditForm({ ...editForm, helpText: event.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="A short hint shown below the field"
              />
            </div>
            <label className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
              <input
                type="checkbox"
                checked={editForm.isRequired}
                onChange={(event) =>
                  setEditForm({ ...editForm, isRequired: event.target.checked })
                }
                className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600"
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  Students must answer this field
                </span>
                <span className="mt-0.5 block text-xs text-gray-500">
                  Turn this off to make the answer optional.
                </span>
              </span>
            </label>
            <ColorRulesEditor
              fieldType={editingField.field_type}
              options={editingField.options || []}
              value={editForm.colorRules}
              onChange={(colorRules) =>
                setEditForm({ ...editForm, colorRules })
              }
            />
            <label className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
              <input
                type="checkbox"
                checked={editForm.showOnDashboardByDefault}
                onChange={(event) =>
                  setEditForm({
                    ...editForm,
                    showOnDashboardByDefault: event.target.checked,
                  })
                }
                className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600"
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  Show on dashboard by default
                </span>
                <span className="mt-0.5 block text-xs text-gray-500">
                  This applies until a user saves their own Column Settings choice.
                </span>
              </span>
            </label>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 rounded-lg bg-blue-600 px-4 py-3 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save changes"}
              </button>
              <button
                type="button"
                onClick={closeEdit}
                className="flex-1 rounded-lg bg-gray-200 px-4 py-3 font-medium text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FormFieldsPage;
